import React, { FC, useState } from "react";
import { Button } from "primereact/button";
import { easasConfirmDialog } from "components/fragments/easas-global-fragment/EasasGlobalConfirmDialog";
import { InputNumber } from "primereact/inputnumber";
import ColorPicker from "components/basic/color-picker/ColorPicker";
import { Column, ColumnEditorOptions } from "primereact/column";
import { DataTable, DataTableRowEditCompleteEvent } from "primereact/datatable";
import { T } from "services/lang/LanguageService";
import { DialIndicatorEditorProps, Zone } from "./DialIndicatorTypes";
import { getHexRandomColor } from "../charts/utils";
import "./DialIndicatorEditor.scss";

const DialIndicatorZonesEditor: FC<DialIndicatorEditorProps> = ({ config, onConfigChange }) => {
  const [zones, setZones] = useState<Zone[]>(config.zones ?? []);

  const isDuplicateValue = (value: number, index: number) =>
    zones.some((z, i) => z.maxValue === value && i !== index);

  const onRowEditComplete = (e: DataTableRowEditCompleteEvent) => {
    const { newData, index } = e;
    const oldValue = zones[index];

    if (newData.maxValue !== oldValue.maxValue && isDuplicateValue(newData.maxValue, index)) {
      return;
    }

    const updatedZones = [...zones];
    updatedZones[index] = newData as Zone;

    const sortedZones = updatedZones.sort((a, b) => a.maxValue - b.maxValue);
    setZones(sortedZones);
    onConfigChange({ ...config, zones: sortedZones });
  };

  const findUniqueMaxValue = () => {
    let max = 0;
    zones.forEach(zone => {
      if (zone.maxValue > max) max = zone.maxValue;
    });
    return max + 1;
  };

  const addZone = () => {
    const newZone: Zone = {
      color: getHexRandomColor(),
      maxValue: findUniqueMaxValue(),
    };
    const updated = [...zones, newZone];
    setZones(updated);
    onConfigChange({ ...config, zones: updated });
  };

  const deleteZone = (currentZone: Zone) => {
    easasConfirmDialog({
      header: T("action.delete"),
      message: T("action.delete-zone"),
      confirmedCallback: () => {
        const updated = zones.filter(zone => zone.maxValue !== currentZone.maxValue);
        setZones(updated);
        onConfigChange({ ...config, zones: updated });
      },
    });
  };

  const colorEditor = (options: ColumnEditorOptions) => {
    return (
      <ColorPicker
        value={options.value}
        onValueChange={e => options.editorCallback?.(e.target.value)}
      />
    );
  };

  const maxValueEditor = (options: ColumnEditorOptions) => {
    const isInvalid = isDuplicateValue(options.value, options.rowIndex ?? -1);
    return (
      <div className="flex flex-column gap-1">
        <InputNumber
          value={options.value}
          onValueChange={e => options.editorCallback?.(e.value ?? 0)}
          invalid={isInvalid}
          className="w-10rem"
        />
        {isInvalid && <small className="p-error">{T("message.zone-value-already-exists")}</small>}
      </div>
    );
  };

  const colorBodyTemplate = (zone: Zone) => {
    return <div className="color-body-template" style={{ backgroundColor: zone.color }} />;
  };

  const zoneDeleteButtonTemplate = (zone: Zone) => {
    return (
      <Button
        icon="pi pi-trash"
        className="dashboard-editor-button icon-red"
        onClick={() => deleteZone(zone)}
      />
    );
  };

  return (
    <div className="flex flex-column gap-3">
      <Button
        label={T("action.add-zone")}
        icon="pi pi-plus"
        className="component-editor-button icon-green w-max"
        onClick={addZone}
      />
      <DataTable
        value={zones}
        editMode="row"
        dataKey="maxValue"
        onRowEditComplete={onRowEditComplete}
        size="small"
      >
        <Column
          field="color"
          header={T("label.zone-color")}
          body={colorBodyTemplate}
          editor={colorEditor}
          style={{ width: "25%" }}
        />
        <Column
          field="maxValue"
          header={T("label.max-value")}
          editor={maxValueEditor}
          style={{ width: "55%" }}
        />
        <Column
          rowEditor
          style={{
            width: "10%",
            textAlign: "end",
          }}
        />
        <Column
          header={T("action.delete")}
          style={{
            width: "10%",
          }}
          body={zoneDeleteButtonTemplate}
        />
      </DataTable>
    </div>
  );
};

export default DialIndicatorZonesEditor;
